import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { conexionesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Plug, Plus, Trash2, Loader2, Save, CheckCircle2, XCircle, Eye, EyeOff, Building2, Boxes,
  ChevronDown, ChevronRight, Pencil, BookOpen, Link2,
} from 'lucide-react';
import { toast } from 'sonner';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import Capacidades from '@/components/Capacidades';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';
import IngresoPorMicrosoft from '@/components/IngresoPorMicrosoft';

// El alcance vacio es "todo el tenant". Es `''` y no `null` a proposito: con `null`, el
// indice unico de Mongo no distingue documentos. Ver `models/conexiones.py`.
const TODO_EL_TENANT = '';

/**
 * Conexiones: un sistema externo mas su credencial.
 *
 * Esta pantalla existe para que dar de alta una integracion **no requiera saber como
 * autentica cada proveedor**. Que GitHub use `Authorization: Bearer` y GitLab un header
 * propio llamado `PRIVATE-TOKEN` sale de la plantilla, no del admin.
 *
 * Y la prueba se hace **antes de guardar**: pegar el token, probar, y recien entonces
 * guardar. Guardar primero y descubrir despues que la credencial estaba mal es como se
 * llegaba a un 401 en el turno de un agente, tres dias mas tarde.
 */
/**
 * Qué va a saber hacer esta conexión, **antes** de darla de alta.
 *
 * Sin esto, agregar un sistema es cargar un token a ciegas y descubrir después qué trae — y lo
 * que trae es la mitad de la decisión de agregarlo. Las herramientas las escribe y las prueba el
 * producto: una integración que existe en el mundo hace diez años no puede ser tarea de cada
 * cliente.
 *
 * **Se distingue lo que lee de lo que escribe**, porque no es lo mismo dejar que un agente mire
 * el código que dejarlo abrir incidencias en un sistema que ve un equipo entero.
 *
 * **Y se dice sobre qué recurso trabaja cada una.** Cuál proyecto no se configura acá: lo ata
 * cada cápsula en su vínculo. Con el recurso adentro de la conexión, dos proyectos del mismo
 * GitLab necesitarían dos copias del mismo token — que es el problema que la conexión vino a
 * resolver.
 */
/**
 * El texto del cartel de borrar, armado con lo que de verdad depende de esta conexión.
 *
 * **Lo que no se puede mirar se dice.** «No se rompe nada» porque una consulta falló es
 * exactamente el permiso que alguien necesitaba para borrar algo que sí se usaba.
 */
/**
 * Para qué sirve esta conexión, sin tener que amenazar con borrarla.
 *
 * **La respuesta ya existía y sólo se veía en el cartel de eliminar** — el único momento en que
 * nadie la está buscando. Quien abre una conexión suele venir con la pregunta contraria: «¿esto
 * para qué es?, ¿lo puedo tocar?». Es la misma consulta y responde las dos.
 *
 * **Una conexión que no usa nadie se dice con todas las letras.** Es información, no ausencia de
 * información: significa que se puede tocar tranquilo, y ese es el dato más difícil de conseguir
 * de otra forma.
 */
function QuienLaUsa({ usos }) {
  if (!usos) return null;

  if (usos.no_se_pudo_mirar?.length) {
    return (
      <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2"
         data-testid="quien-la-usa">
        No se pudo averiguar qué depende de esta conexión ({usos.no_se_pudo_mirar.join(', ')}).
        <b> No quiere decir que no la use nadie.</b>
      </p>
    );
  }

  if (!usos.usos?.length) {
    return (
      <p className="text-[10px] text-zinc-500 bg-zinc-50 border border-brand-border rounded-sm p-2"
         data-testid="quien-la-usa">
        Hoy no la usa nadie: ningún vínculo, herramienta, repositorio ni canal la nombra.
      </p>
    );
  }

  return (
    <div className="text-[10px] bg-zinc-50 border border-brand-border rounded-sm p-2 space-y-1"
         data-testid="quien-la-usa">
      <p className="font-semibold text-zinc-600 uppercase tracking-wider text-[9px]">
        De esto depende ({usos.total})
      </p>
      {usos.usos.map((g) => (
        <div key={g.tipo} className="flex items-start gap-1.5">
          <Link2 className="w-2.5 h-2.5 mt-1 shrink-0 text-zinc-400" />
          <span className="text-zinc-600">
            <b>{g.items.length} {g.tipo}</b>: {g.items.map((i) => i.resumen).join(', ')}
            <span className="text-zinc-400"> — si se va, {g.que_se_rompe}.</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function describirUsos(conexionId, usos) {
  const partes = [`Se elimina "${conexionId}".`];

  if (usos?.no_se_pudo_mirar?.length) {
    partes.push(
      `No se pudo averiguar qué depende de esta conexión (${usos.no_se_pudo_mirar.join(', ')}), ` +
      'así que no se puede afirmar que no se rompa nada.',
    );
  }

  const grupos = usos?.usos || [];
  if (!grupos.length && !usos?.no_se_pudo_mirar?.length) {
    partes.push('Hoy no la usa nadie: ningún vínculo, herramienta, repositorio ni canal la nombra.');
    return partes.join(' ');
  }

  for (const g of grupos) {
    const cuantos = g.items.length;
    // **El `resumen` lo manda el backend.** La pantalla no puede adivinar cómo se nombra un uso:
    // el primer campo de un vínculo es la cápsula, y una lista que dice "sistemas, sistemas,
    // sistemas" no informa nada — que es justo lo que pasaba con los tres de GitLab.
    const ejemplos = g.items.slice(0, 4).map((i) => i.resumen).join(', ');
    partes.push(
      `${cuantos} ${g.tipo}${cuantos === 1 ? '' : ''} (${ejemplos}${cuantos > 4 ? `, +${cuantos - 4}` : ''}): ${g.que_se_rompe}.`,
    );
  }
  partes.push('Vuelve a funcionar recreando la conexión con la misma credencial.');
  return partes.join(' ');
}

function QueSabeHacer({ herramientas, plantillaId }) {
  const [abierto, setAbierto] = useState(false);
  const lista = herramientas || [];

  if (!lista.length) {
    return (
      <p className="text-[10px] text-zinc-400 mt-1.5">
        Todavía no trae herramientas: se conecta, y lo que sepa hacer se declara aparte.
      </p>
    );
  }

  const escriben = lista.filter((h) => h.escribe).length;
  const recursos = [...new Set(lista.flatMap((h) => h.del_vinculo || []))];

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        data-testid={`que-sabe-hacer-${plantillaId}`}
        className="text-[10px] text-brand-primary hover:text-[#001F6D] flex items-center gap-1"
      >
        {abierto ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {lista.length} herramientas, {escriben} de ellas escriben
      </button>

      {abierto && (
        <div className="mt-1.5 space-y-1 pl-4">
          {lista.map((h) => (
            <div key={h.nombre} className="flex items-start gap-1.5">
              {h.escribe
                ? <Pencil className="w-2.5 h-2.5 mt-1 text-amber-600 shrink-0" />
                : <BookOpen className="w-2.5 h-2.5 mt-1 text-zinc-400 shrink-0" />}
              <div className="flex-1">
                <span className="text-[10px] text-zinc-700">{h.descripcion}</span>
                <code className="text-[9px] text-zinc-400 ml-1.5">{h.nombre}</code>
              </div>
            </div>
          ))}
          {recursos.length > 0 && (
            <p className="text-[10px] text-zinc-400 flex items-start gap-1 pt-1">
              <Link2 className="w-2.5 h-2.5 mt-1 shrink-0" />
              <span>
                Sobre cuál {recursos.join(', ').toLowerCase()} trabajan <b>no se decide acá</b>:
                lo ata cada cápsula en su vínculo. Así dos proyectos del mismo sistema comparten
                una sola credencial.
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ConexionesPage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || '';

  const [plantillas, setPlantillas] = useState([]);
  // Cómo puede autenticarse un sistema y **qué credenciales pide cada forma**. Lo manda el
  // backend: armarlo acá sería la segunda copia, y la que se queda vieja al agregar un modo.
  const [modosDeAuth, setModosDeAuth] = useState([]);
  const [conexiones, setConexiones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [usos, setUsos] = useState(null);
  const [valores, setValores] = useState({});
  const [verSecreto, setVerSecreto] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [confirmar, setConfirmar] = useState({ open: false });
  // Las dos mitades de la misma historia: con que llegamos a un sistema, y que sabemos hacer
  // con el. Separarlas en dos pantallas obligaria a ir y venir para entender una sola cosa.
  const [seccion, setSeccion] = useState('conexiones');
  const capsuleId = new URLSearchParams(window.location.search).get('capsule') || 'sistemas';
  const [abriendo, setAbriendo] = useState(false);

  // Cual conexion se pidio ultima. `editar` es asincrono, y sin esto **gana la respuesta que
  // llega ultima, no la que el usuario eligio ultima**: dos clics seguidos dejan el editor
  // mostrando la conexion equivocada. Paso el 2026-08-22, en la primera prueba de esta
  // pantalla: se hizo clic en GitLab, el editor volvio a GitHub, y el cartel verde de GitHub
  // se leyo como si GitLab anduviera. Una conexion inalcanzable parecio sana.
  const seleccionada = useRef(null);

  const cargar = useCallback(async () => {
    if (!tenantId) return;
    setCargando(true);
    try {
      const [p, c] = await Promise.all([conexionesAPI.plantillas(), conexionesAPI.list(tenantId)]);
      setModosDeAuth(p.data?.modos_de_auth || []);
      setPlantillas(p.data?.plantillas || []);
      setConexiones(c.data || []);
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudieron cargar las conexiones');
    } finally {
      setCargando(false);
    }
  }, [tenantId]);

  useEffect(() => { cargar(); }, [cargar]);

  const desdePlantilla = (plantilla) => {
    // **Una conexión propia no puede llamarse como su plantilla.** `propia` es el molde, no el
    // sistema: dos APIs distintas del mismo cliente chocarían por el id. Se deja vacío para que
    // lo escriba quien la da de alta, y por eso tampoco se marca como "ya está".
    const enBlanco = plantilla.en_blanco === true;
    seleccionada.current = `${plantilla.plantilla_id}::${TODO_EL_TENANT}`;
    setEditando({
      conexion_id: enBlanco ? '' : plantilla.plantilla_id,
      en_blanco: enBlanco,
      plantilla_id: plantilla.plantilla_id,
      nombre: enBlanco ? '' : plantilla.nombre,
      descripcion: enBlanco ? '' : plantilla.descripcion,
      tipo: plantilla.tipo || 'rest',
      base_url: plantilla.base_url || '',
      config_schema: plantilla.config_schema || [],
      auth_modo: plantilla.auth?.modo || 'ninguna',
      auth_nombre_del_campo: plantilla.auth?.nombre_del_campo || '',
      prueba: plantilla.prueba || {},
      capsule_id: TODO_EL_TENANT,
      activa: true,
      ayuda: plantilla.ayuda || '',
      esNueva: true,
    });
    setValores({});
    setResultado(null);
  };

  const editar = async (fila) => {
    const clave = `${fila.conexion_id}::${fila.capsule_id || TODO_EL_TENANT}`;
    seleccionada.current = clave;
    // El resultado anterior se va **antes** de pedir la nueva: si quedara en pantalla mientras
    // carga otra conexion, se lee como el resultado de esta.
    setResultado(null);
    setAbriendo(true);
    try {
      const { data } = await conexionesAPI.get(fila.conexion_id, tenantId, fila.capsule_id || TODO_EL_TENANT);
      // Llego tarde: mientras viajaba, el usuario eligio otra. No manda.
      if (seleccionada.current !== clave) return;
      const plantilla = plantillas.find((p) => p.plantilla_id === data.plantilla_id);
      setEditando({ ...data, ayuda: plantilla?.ayuda || '', esNueva: false });
      setValores(data.config_values || {});
      // **Para qué sirve esta conexión, sin tener que amenazar con borrarla.** La respuesta ya
      // existía pero sólo se veía en el cartel de eliminar, que es el único momento en que nadie
      // la está buscando.
      setUsos(null);
      conexionesAPI.usos(fila.conexion_id, tenantId)
        .then(({ data: u }) => { if (seleccionada.current === clave) setUsos(u); })
        .catch(() => { if (seleccionada.current === clave) setUsos(null); });
    } catch (err) {
      if (seleccionada.current === clave) toast.error(formatApiError(err) || 'No se pudo abrir la conexion');
    } finally {
      if (seleccionada.current === clave) setAbriendo(false);
    }
  };

  /**
   * Si esta conexión la declaró el cliente (y no viene de una plantilla del producto).
   *
   * **No se puede usar `en_blanco`**: eso sólo es cierto en el instante de elegir la plantilla en
   * blanco, así que al reabrir una conexión guardada los campos desaparecían.
   */
  const esPropia = !!editando && (editando.en_blanco || editando.plantilla_id === 'propia'
                                  || !editando.plantilla_id);

  const cuerpo = () => ({
    tenant_id: tenantId,
    capsule_id: editando.capsule_id || TODO_EL_TENANT,
    nombre: editando.nombre || '',
    descripcion: editando.descripcion || '',
    tipo: editando.tipo || 'rest',
    base_url: editando.base_url || '',
    config_schema: editando.config_schema || [],
    config_values: valores,
    token_url: editando.token_url || '',
    auth_del_cliente: editando.auth_del_cliente || 'cuerpo',
    scope: editando.scope || '',
    auth_modo: editando.auth_modo || 'ninguna',
    auth_nombre_del_campo: editando.auth_nombre_del_campo || '',
    prueba: editando.prueba || {},
    plantilla_id: editando.plantilla_id || '',
    activa: editando.activa !== false,
  });

  /**
   * Una conexión propia **no se puede guardar ni probar sin nombre corto**: es su identificador,
   * y sin él el `PUT` se iría a `/conexiones/` — un error de ruta que se lee como cualquier otra
   * cosa. El aviso va al lado del botón y no en un toast, que es la doctrina de esta pantalla.
   */
  const faltaElNombre = () => {
    if (String(editando?.conexion_id || '').trim()) return false;
    toast.error('Ponele un nombre corto a la conexión: es cómo la van a nombrar las herramientas.');
    return true;
  };

  const probar = async () => {
    if (faltaElNombre()) return;
    setProbando(true);
    setResultado(null);
    try {
      // Se guarda primero si es nueva: probar necesita que la conexion exista para leer lo que
      // ya estuviera cargado. Lo que el formulario tenga escrito le gana igual.
      if (editando.esNueva) await conexionesAPI.save(editando.conexion_id, cuerpo());
      const { data } = await conexionesAPI.probar(editando.conexion_id, {
        tenant_id: tenantId,
        capsule_id: editando.capsule_id || TODO_EL_TENANT,
        config_values: valores,
      });
      // El resultado queda firmado con **cual** conexion se probo y contra **que URL**. Sin la
      // firma, un cartel verde no dice de quien es, y ese fue exactamente el modo de fallar.
      setResultado({ ...data, deQuien: editando.conexion_id, contraQue: editando.base_url });
      if (editando.esNueva) { setEditando({ ...editando, esNueva: false }); cargar(); }
    } catch (err) {
      setResultado({
        ok: false, paso: 'red', deQuien: editando.conexion_id, contraQue: editando.base_url,
        mensaje: formatApiError(err) || 'No se pudo probar',
      });
    } finally {
      setProbando(false);
    }
  };

  const guardar = async () => {
    if (faltaElNombre()) return;
    setGuardando(true);
    try {
      await conexionesAPI.save(editando.conexion_id, cuerpo());
      toast.success(`Conexion "${editando.nombre || editando.conexion_id}" guardada`);
      setEditando(null);
      cargar();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  /**
   * **El cartel dice qué se rompe, no que algo podría romperse.**
   *
   * Antes avisaba *"las herramientas que la nombren van a fallar"*: cierto, y sin utilidad. No
   * decía cuáles, no decía quién las usa, y quien lo leía no podía decidir nada — así que lo
   * aceptaba sin leer, y después aparecía un agente que dejó de poder buscar código y nadie lo
   * asociaba.
   *
   * Se pregunta **antes** de mostrar el cartel, aunque cueste una llamada: un cartel de
   * confirmación que se dibuja sin saber la respuesta no es una confirmación, es un trámite.
   */
  const borrar = async (fila) => {
    let usos = null;
    try {
      const { data } = await conexionesAPI.usos(fila.conexion_id, tenantId);
      usos = data;
    } catch (_) {
      // Si no se pudo averiguar, se dice **eso** y no "no se rompe nada".
      usos = { no_se_pudo_mirar: ['todo'], usos: [], total: 0 };
    }
    setConfirmar({
      open: true,
      title: 'Eliminar conexion',
      description: describirUsos(fila.conexion_id, usos),
      confirmText: 'Eliminar',
    onConfirm: async () => {
      try {
        await conexionesAPI.remove(fila.conexion_id, tenantId, fila.capsule_id || TODO_EL_TENANT);
        toast.success('Conexion eliminada');
        if (editando?.conexion_id === fila.conexion_id) setEditando(null);
        cargar();
      } catch (err) {
        toast.error(formatApiError(err) || 'No se pudo eliminar');
      } finally {
        setConfirmar({ open: false });
      }
      },
    });
  };

  // La plantilla en blanco **nunca** está "ya agregada": se puede usar tantas veces como
  // sistemas propios tenga la organización.
  const yaExiste = (plantillaId) =>
    plantillaId !== 'propia' && conexiones.some((c) => c.conexion_id === plantillaId);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-brand-primary" />
        <div>
          <h1 className="text-lg font-bold text-[#09090B]">Conexiones</h1>
          <p className="text-[11px] text-zinc-500">
            Un sistema externo mas su credencial. Las skills la nombran; el secreto vive aca.
          </p>
        </div>
      </div>

      <QueSeConfiguraAca
        testId="ayuda-conexiones"
        queEs="Un sistema de afuera más la credencial para entrar: la API del banco, el GitLab de la empresa, un servicio de clima."
        paraQue="Para que la credencial viva en un solo lugar. Las herramientas la nombran, no la copian: cuando se rota un token, se cambia acá y funcionan todas."
        aQueAfecta={<>A <b>todas las herramientas</b> que apuntan a esta conexión. Cambiar la URL o la credencial las cambia a todas de una vez — y romperla las rompe a todas también, por eso al borrar se avisa cuántas la usan.</>}
        ejemplo={<>«Libro Banco DESA» guarda la URL, el client id y el secreto de OAuth2. La herramienta que consulta saldos sólo dice «uso esa conexión»; nunca ve el secreto.</>}
        ojo={<>El secreto se guarda cifrado y <b>no se vuelve a mostrar</b>. Nunca va en la definición de la herramienta ni en la prosa de la habilidad: esas dos se exportan, se versionan y se muestran en pantalla.</>}
      />

      <div className="flex gap-1 border-b border-brand-border">
        {[
          ['conexiones', 'Conexiones'],
          ['capacidades', 'Capacidades y vinculos'],
          // El ingreso es de la plataforma entera, no de esta capsula. Solo un admin puede
          // leerlo, asi que a los demas la solapa ni les aparece: un boton que siempre
          // devuelve 403 se lee como que algo esta roto.
          ...(user?.role === 'admin' ? [['ingreso', 'Ingreso por Microsoft']] : []),
        ].map(([k, t]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setSeccion(k)}
                  className={`text-xs px-3 py-1.5 rounded-t-sm border-b-2 ${
                    seccion === k ? 'border-brand-primary text-brand-primary font-semibold'
                                  : 'border-transparent text-zinc-500'}`}>
            {t}
          </button>
        ))}
      </div>

      {seccion === 'ingreso' && <IngresoPorMicrosoft />}

      {seccion === 'capacidades' && (
        <Capacidades tenantId={tenantId} capsuleId={capsuleId} conexiones={conexiones} />
      )}

      {seccion === 'conexiones' && (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lo que hay */}
        <Card className="rounded-sm border-brand-border">
          <CardHeader className="py-2 px-3 border-b border-brand-border">
            <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600">
              Configuradas
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {cargando && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
            {!cargando && conexiones.length === 0 && (
              <p className="text-xs text-zinc-400">
                Todavia no hay ninguna. Elegi un sistema de la derecha para empezar.
              </p>
            )}
            {conexiones.map((c) => (
              <div
                key={`${c.conexion_id}-${c.capsule_id}`}
                data-testid={`conexion-${c.conexion_id}`}
                className="flex items-center justify-between border border-brand-border rounded-sm px-3 py-2"
              >
                <button className="text-left flex-1" onClick={() => editar(c)}>
                  <div className="text-xs font-semibold text-[#09090B]">{c.nombre || c.conexion_id}</div>
                  <div className="text-[10px] text-zinc-500 font-mono">{c.base_url || 'sin URL'}</div>
                </button>
                <div className="flex items-center gap-1">
                  {/* Que una conexion sea del tenant o de una capsula cambia a quien alcanza:
                      mostrarlo evita la pregunta "por que esta capsula usa otro token". */}
                  <Badge variant="outline" className="text-[9px] rounded-sm gap-1">
                    {c.alcance === 'capsula' ? <Boxes className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                    {c.alcance === 'capsula' ? c.capsule_id : 'todo el tenant'}
                  </Badge>
                  {c.activa === false && (
                    <Badge variant="outline" className="text-[9px] rounded-sm text-zinc-400">inactiva</Badge>
                  )}
                  <Button
                    variant="ghost" size="sm" data-testid={`borrar-${c.conexion_id}`}
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500"
                    onClick={() => borrar(c)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Lo que se puede agregar */}
        <Card className="rounded-sm border-brand-border">
          <CardHeader className="py-2 px-3 border-b border-brand-border">
            <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600">
              Sistemas disponibles
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {plantillas.map((p) => (
              <div key={p.plantilla_id} className="border border-brand-border rounded-sm px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-[#09090B]">{p.nombre}</div>
                    <div className="text-[10px] text-zinc-500">{p.descripcion}</div>
                  </div>
                  <Button
                    size="sm" variant="outline" data-testid={`agregar-${p.plantilla_id}`}
                    className="h-7 text-[10px] rounded-sm gap-1"
                    onClick={() => desdePlantilla(p)}
                    disabled={yaExiste(p.plantilla_id)}
                  >
                    <Plus className="w-3 h-3" />
                    {yaExiste(p.plantilla_id) ? 'Ya esta' : 'Agregar'}
                  </Button>
                </div>
                <QueSabeHacer herramientas={p.herramientas} plantillaId={p.plantilla_id} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* El formulario */}
      {editando && (
        <Card className="rounded-sm border-brand-border" data-testid="editor-conexion">
          <CardHeader className="py-2 px-3 border-b border-brand-border flex-row items-center justify-between">
            <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-zinc-600">
              {editando.nombre || editando.conexion_id}
            </CardTitle>
            <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setEditando(null)}>
              Cerrar
            </Button>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            {editando.ayuda && (
              <p className="text-[10px] text-zinc-500 bg-zinc-50 border border-brand-border rounded-sm p-2">
                {editando.ayuda}
              </p>
            )}

            {!editando.esNueva && <QuienLaUsa usos={usos} />}

            {esPropia && editando.esNueva && (
              <div className="flex items-center gap-2">
                <Label className="text-[10px] w-32 text-zinc-600 shrink-0">Nombre corto</Label>
                <Input
                  data-testid="conexion-id-propia"
                  value={editando.conexion_id || ''}
                  onChange={(e) => setEditando({
                    ...editando,
                    conexion_id: e.target.value.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-'),
                  })}
                  placeholder="reportes-cyb"
                  className="rounded-sm text-xs h-7 font-mono"
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <Label className="text-[10px] w-32 text-zinc-600 shrink-0">URL base</Label>
              <Input
                data-testid="conexion-base-url"
                value={editando.base_url || ''}
                onChange={(e) => setEditando({ ...editando, base_url: e.target.value })}
                className="rounded-sm text-xs h-7 font-mono"
                placeholder="https://api.ejemplo.com"
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-[10px] w-32 text-zinc-600 shrink-0">Alcance</Label>
              <Input
                data-testid="conexion-alcance"
                value={editando.capsule_id || ''}
                onChange={(e) => setEditando({ ...editando, capsule_id: e.target.value })}
                className="rounded-sm text-xs h-7 font-mono"
                placeholder="vacio = todo el tenant; o el id de una capsula"
              />
            </div>

            {/* **Se muestra para cualquier conexión propia, no sólo al crearla.** Estaba atado a
                `en_blanco`, que sólo es cierto en el momento de elegir la plantilla en blanco: al
                volver a abrir una conexión guardada, los campos de OAuth2 —incluido cómo viaja la
                credencial— **desaparecían y no había forma de corregirlos**. Le pasó a Diego el
                2026-08-25: su conexión quedó en «cuerpo» y la pantalla no tenía dónde volverla a
                «basic». Una primitiva que se puede crear y no editar es media primitiva. */}
            {esPropia && (
              <>
                {/* **Cómo se prueba también lo declara quien la da de alta.** No se puede
                    adivinar: cada API tiene su endpoint liviano. Sin esto, «Probar» contesta
                    «esta conexión no declara cómo probarse» y **no había dónde declararlo** — le
                    pasó a Diego el 2026-08-25 y concluyó, con razón, que el probador era genérico
                    y no servía para la suya. */}
                <div className="flex items-center gap-2">
                  <Label className="text-[10px] w-32 text-zinc-600 shrink-0">Ruta de prueba</Label>
                  <div className="flex-1">
                    <Input
                      data-testid="conexion-ruta-de-prueba"
                      value={editando.prueba?.ruta || ''}
                      onChange={(e) => setEditando({
                        ...editando,
                        prueba: { ...(editando.prueba || {}), metodo: 'GET', ruta: e.target.value },
                      })}
                      className="rounded-sm text-xs h-7 font-mono"
                      placeholder="/libro-banco"
                    />
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      Cualquier GET liviano que conteste con la credencial puesta. Es lo que
                      «Probar» va a pedir; si está vacío, no hay contra qué ir.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Label className="text-[10px] w-32 text-zinc-600 shrink-0 pt-1.5">Cómo se autentica</Label>
                  <div className="flex-1">
                    <select
                      data-testid="conexion-auth-modo"
                      value={editando.auth_modo || 'ninguna'}
                      onChange={(e) => {
                        const modo = e.target.value;
                        const elegido = modosDeAuth.find((m) => m.modo === modo);
                        // **Los campos salen del modo, no se declaran.** Pedirle a alguien que
                        // declare que OAuth2 necesita un client id y un secret es pedirle que
                        // repita lo que ya dijo al elegir el modo — y es justo donde se traba
                        // quien no escribió un cliente OAuth2 antes.
                        setEditando({ ...editando, auth_modo: modo, config_schema: elegido?.campos || [] });
                      }}
                      className="h-7 w-full text-[11px] border border-brand-border rounded-sm px-1 bg-white"
                    >
                      {modosDeAuth.map((m) => (
                        <option key={m.modo} value={m.modo}>{m.nombre}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {modosDeAuth.find((m) => m.modo === (editando.auth_modo || 'ninguna'))?.detalle}
                    </p>
                  </div>
                </div>

                {['api_key_header', 'api_key_query'].includes(editando.auth_modo) && (
                  <div className="flex items-center gap-2">
                    <Label className="text-[10px] w-32 text-zinc-600 shrink-0">
                      {editando.auth_modo === 'api_key_header' ? 'Nombre del header' : 'Nombre del parámetro'}
                    </Label>
                    <Input
                      data-testid="conexion-auth-campo"
                      value={editando.auth_nombre_del_campo || ''}
                      onChange={(e) => setEditando({ ...editando, auth_nombre_del_campo: e.target.value })}
                      className="rounded-sm text-xs h-7 font-mono"
                      placeholder="PRIVATE-TOKEN"
                    />
                  </div>
                )}

                {editando.auth_modo === 'oauth2_client_credentials' && (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] w-32 text-zinc-600 shrink-0">Dónde se pide el token</Label>
                      <Input
                        data-testid="conexion-token-url"
                        value={editando.token_url || ''}
                        onChange={(e) => setEditando({ ...editando, token_url: e.target.value })}
                        className="rounded-sm text-xs h-7 font-mono"
                        placeholder="https://…/oauth/token"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-[10px] w-32 text-zinc-600 shrink-0">Alcance (scope)</Label>
                      <Input
                        data-testid="conexion-scope"
                        value={editando.scope || ''}
                        onChange={(e) => setEditando({ ...editando, scope: e.target.value })}
                        className="rounded-sm text-xs h-7 font-mono"
                        placeholder="vacío si el sistema no lo pide"
                      />
                    </div>
                    <div className="flex items-start gap-2">
                      <Label className="text-[10px] w-32 text-zinc-600 shrink-0 pt-1.5">Cómo viaja</Label>
                      <div className="flex-1">
                        <select
                          data-testid="conexion-auth-del-cliente"
                          value={editando.auth_del_cliente || 'cuerpo'}
                          onChange={(e) => setEditando({ ...editando, auth_del_cliente: e.target.value })}
                          className="h-7 w-full text-[11px] border border-brand-border rounded-sm px-1 bg-white"
                        >
                          <option value="cuerpo">En el cuerpo del pedido</option>
                          <option value="basic">Como Basic Auth en el header</option>
                        </select>
                        <p className="text-[10px] text-zinc-400 mt-0.5">
                          Si no sabés cuál, dejá la primera y apretá <b>Probar</b>: si el
                          proveedor la rechaza, te dice cuál acepta.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            {(editando.config_schema || []).map((campo) => (
              <div key={campo.key} className="flex items-center gap-2">
                <Label className="text-[10px] w-32 text-zinc-600 shrink-0">{campo.label || campo.key}</Label>
                <div className="flex-1 relative">
                  <Input
                    data-testid={`conexion-campo-${campo.key}`}
                    type={campo.secret && !verSecreto[campo.key] ? 'password' : 'text'}
                    value={valores[campo.key] || ''}
                    onChange={(e) => setValores({ ...valores, [campo.key]: e.target.value })}
                    className="rounded-sm text-xs h-7 pr-8 font-mono"
                    placeholder={campo.required ? 'Requerido' : 'Opcional'}
                  />
                  {campo.secret && (
                    <button
                      type="button"
                      className="absolute right-2 top-1.5 text-zinc-400 hover:text-zinc-600"
                      onClick={() => setVerSecreto({ ...verSecreto, [campo.key]: !verSecreto[campo.key] })}
                    >
                      {verSecreto[campo.key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Solo el resultado de **esta** conexion. Uno de otra, aunque sea reciente, es
                informacion que induce al error que ya cometimos una vez. */}
            {resultado && resultado.deQuien === editando.conexion_id && (
              <div
                data-testid="resultado-prueba"
                className={`text-[11px] rounded-sm border p-2 flex gap-2 items-start ${
                  resultado.ok ? 'border-green-200 bg-green-50 text-green-800'
                               : 'border-red-200 bg-red-50 text-red-800'
                }`}
              >
                {resultado.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                <div>
                  <div className="font-semibold" data-testid="resultado-de-quien">
                    {editando.nombre || editando.conexion_id}
                    {resultado.contraQue ? <span className="font-normal opacity-70"> · {resultado.contraQue}</span> : null}
                  </div>
                  <div>{resultado.mensaje}</div>
                  {/* El paso donde fallo es la mitad del diagnostico: no es lo mismo que no se
                      llegue al servidor que que el servidor rechace la credencial. */}
                  {!resultado.ok && resultado.paso && (
                    <div className="text-[10px] opacity-70 mt-1">Fallo en: {resultado.paso}</div>
                  )}

                  {/* **El diagnóstico que se puede apretar.** «El proveedor rechazó la
                      credencial» es cierto y no accionable: quien lo lee no tiene forma de saber
                      que el secreto está bien y lo que está mal es cómo viaja. Acá el probador ya
                      averiguó cuál acepta el sistema, así que la corrección es un clic. */}
                  {resultado.arreglo_sugerido && (
                    <div className="mt-2 border-t border-red-200 pt-2 space-y-1">
                      <div className="text-[11px]">{resultado.arreglo_sugerido.mensaje}</div>
                      <Button
                        size="sm" data-testid="aplicar-arreglo-sugerido"
                        className="h-6 text-[10px] rounded-sm bg-brand-primary text-white"
                        onClick={() => {
                          setEditando({
                            ...editando,
                            [resultado.arreglo_sugerido.campo]: resultado.arreglo_sugerido.valor,
                          });
                          setResultado(null);
                          toast.success('Cambiado. Probá de nuevo.');
                        }}
                      >
                        Cambiarlo y volver a probar
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                size="sm" variant="outline" data-testid="probar-conexion"
                className="h-7 text-[10px] rounded-sm" onClick={probar} disabled={probando || guardando || abriendo}
              >
                {probando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Probar conexion
              </Button>
              <Button
                size="sm" data-testid="guardar-conexion"
                className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3"
                onClick={guardar} disabled={guardando || probando || abriendo}
              >
                {guardando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Guardar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      </>
      )}

      <ConfirmActionDialog
        {...confirmar}
        onOpenChange={(open) => !open && setConfirmar({ open: false })}
      />
    </div>
  );
}
